import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, todayISO, ABSENCE_TYPES } from "@/lib/format";
import { StatusBadge } from "@/components/Brand";
import { WeekGrid, Legend, mondayOf, addDays } from "@/components/WeekGrid";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { absenceSpan } from "@/pages/admin/AdminAbsences";
import { OvertimeRequestButton } from "@/components/OvertimeRequest";
import { DayRouteDialog } from "@/components/DayRouteDialog";
import { useAuth, isAdmin } from "@/lib/auth";

const OT_LABEL = { in_attesa: "In attesa di risposta", accettato: "Accettato", rifiutato: "Rifiutato" };

function OvertimeList({ items, onChange }) {
  const { user } = useAuth();
  if (!items.length) return null;
  const cancel = async (o) => {
    if (!window.confirm(`Annullare lo straordinario di ${o.technician_name}?`)) return;
    try { await api.delete(`/admin/overtime/${o.id}`); toast.success("Straordinario annullato"); onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="divide-y rounded-2xl border bg-white" data-testid="overtime-list">
      {items.filter((o) => o.status !== "annullato").map((o, i) => (
        <div key={o.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm" data-testid={`overtime-row-${i}`}>
          <span><b>{o.technician_name}</b> <span className="font-mono text-xs text-slate-500">{fmtDate(o.date, { day: "2-digit", month: "2-digit" })} {o.start}-{o.end}</span>{o.note && <span className="ml-2 text-slate-500">· {o.note}</span>}</span>
          <span className="flex items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${o.status === "accettato" ? "bg-[#0f1710] text-[#a2ca6b]" : o.status === "rifiutato" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-800"}`}>{OT_LABEL[o.status]}</span>
            {isAdmin(user) && <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => cancel(o)} data-testid={`overtime-cancel-${i}`}>Annulla</Button>}
          </span>
        </div>
      ))}
    </div>
  );
}

function WeekView() {
  const { user } = useAuth();
  const [monday, setMonday] = useState(mondayOf(todayISO()));
  const [data, setData] = useState(null);
  const [ot, setOt] = useState([]);
  const [v, setV] = useState(0);
  const [route, setRoute] = useState(null);
  useEffect(() => {
    let live = true;
    setData(null);
    const p = { start: monday, end: addDays(monday, 6) };
    api.get("/admin/attendance", { params: p }).then((r) => live && setData(r.data)).catch((e) => live && toast.error(errMsg(e)));
    api.get("/admin/overtime", { params: p }).then((r) => live && setOt(r.data)).catch(() => live && setOt([]));
    return () => { live = false; };
  }, [monday, v]);
  const reload = () => setV((x) => x + 1);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setMonday((m) => addDays(m, -7))} data-testid="week-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
          <p className="min-w-[160px] text-center font-display font-semibold sm:min-w-[200px]" data-testid="week-range-label">
            {fmtDate(monday, { day: "numeric", month: "short" })} – {fmtDate(addDays(monday, 6), { day: "numeric", month: "short", year: "numeric" })}
          </p>
          <Button variant="outline" size="icon" onClick={() => setMonday((m) => addDays(m, 7))} data-testid="week-next-button"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" onClick={() => setMonday(mondayOf(todayISO()))} data-testid="week-today-button">Questa settimana</Button>
        </div>
        {isAdmin(user) && <OvertimeRequestButton onDone={reload} />}
      </div>
      <Legend />
      {data?.since && <p className="text-xs text-slate-500" data-testid="attendance-since">Presenze rilevate dalle timbrature a partire dal {fmtDate(data.since, { day: "numeric", month: "long", year: "numeric" })}. I giorni futuri mostrano solo permessi e straordinari richiesti.</p>}
      <div className="rounded-2xl border bg-white p-2"><WeekGrid data={data} onCellClick={isAdmin(user) ? (tech, cell) => setRoute({ tech, cell }) : undefined} /></div>
      {isAdmin(user) && <p className="text-xs text-slate-500">Tocca un giorno "Presente" per vedere il percorso (seconda password).</p>}
      <DayRouteDialog target={route} onClose={() => setRoute(null)} />
      <OvertimeList items={ot} onChange={reload} />
    </div>
  );
}

const fmtH = (h) => `${Math.floor(h)}h ${String(Math.round((h % 1) * 60)).padStart(2, "0")}m`;

function DayDetail({ days, i }) {
  const rows = days.filter((c) => c.first_in || c.state === "assente");
  if (!rows.length) return null;
  return (
    <div className="mt-4 overflow-x-auto rounded-xl border" data-testid={`report-${i}-days`}>
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead className="bg-[#f2f7ec] text-xs text-slate-600">
          <tr><th className="px-3 py-2">Giorno</th><th className="px-3 py-2">Entrata</th><th className="px-3 py-2">Uscita</th><th className="px-3 py-2">Pausa</th><th className="px-3 py-2">Ore nette</th><th className="px-3 py-2">Straord.</th><th className="px-3 py-2">Luogo</th></tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((c, j) => (
            <tr key={c.date} className={c.state === "assente" ? "bg-slate-100 text-slate-500" : ""} data-testid={`report-${i}-day-${j}`}>
              <td className="px-3 py-1.5 capitalize">{fmtDate(c.date, { weekday: "short", day: "2-digit", month: "2-digit" })}</td>
              {c.state === "assente" ? <td colSpan={6} className="px-3 py-1.5 font-semibold">Assente</td> : (
                <>
                  <td className="px-3 py-1.5 font-mono">{c.first_in}</td>
                  <td className="px-3 py-1.5 font-mono">{c.last_out || "…"}</td>
                  <td className="px-3 py-1.5 font-mono">{c.lunch_hours > 0 ? fmtH(c.lunch_hours) : "—"}</td>
                  <td className="px-3 py-1.5 font-mono font-semibold">{fmtH(c.hours)}</td>
                  <td className="px-3 py-1.5 font-mono">{c.overtime_hours > 0 ? fmtH(c.overtime_hours) : "—"}</td>
                  <td className="px-3 py-1.5 text-xs">{c.place || "—"}</td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
const STATS = [
  ["working_days", "Giorni lavorativi"], ["present_days", "Presenze"], ["assente_days", "Assenze ingiustificate"],
  ["worked_hours", "Ore lavorate", fmtH], ["overtime_hours", "Straordinario", fmtH],
  ["permesso_days", "Permessi (gg)"], ["permesso_hours", "Permessi (ore)", fmtH], ["malattia_days", "Malattia (gg)"], ["altro_days", "Altro (gg)"],
  ["pending_requests", "Richieste in attesa"], ["jobs", "Interventi"],
];

function PeriodReport() {
  const [techs, setTechs] = useState([]);
  const [q, setQ] = useState({ technician_id: "all", start: todayISO().slice(0, 8) + "01", end: todayISO() });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/technicians").then((r) => setTechs(r.data.filter((t) => t.employment !== "autonomo"))); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const params = { start: q.start, end: q.end, technician_id: q.technician_id === "all" ? undefined : q.technician_id };
      setData((await api.get("/admin/attendance", { params })).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 rounded-2xl border bg-white p-4 sm:grid-cols-[1fr_170px_170px_auto] sm:items-end">
        <div className="space-y-2"><Label>Dipendente</Label>
          <Select value={q.technician_id} onValueChange={(v) => setQ((p) => ({ ...p, technician_id: v }))}>
            <SelectTrigger data-testid="report-tech-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i dipendenti attivi</SelectItem>
              {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>Dal</Label><Input type="date" value={q.start} onChange={(e) => setQ((p) => ({ ...p, start: e.target.value }))} data-testid="report-start-input" /></div>
        <div className="space-y-2"><Label>Al</Label><Input type="date" value={q.end} onChange={(e) => setQ((p) => ({ ...p, end: e.target.value }))} data-testid="report-end-input" /></div>
        <Button onClick={run} disabled={busy || !q.start || !q.end} className="rounded-full" data-testid="report-run-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Interroga
        </Button>
      </div>
      {data && (data.rows.length ? data.rows.map((r, i) => (
        <section key={r.technician.id} className="rounded-2xl border bg-white p-5 rise" data-testid={`report-tech-${i}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-display text-lg font-semibold">{r.technician.name}</h3>
            <p className="font-mono text-xs text-slate-500">{fmtDate(data.start, { day: "2-digit", month: "2-digit", year: "numeric" })} → {fmtDate(data.end, { day: "2-digit", month: "2-digit", year: "numeric" })}</p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
            {STATS.map(([k, l, fmt]) => (
              <div key={k} className={`rounded-xl p-3 ${k === "assente_days" && r.summary[k] ? "bg-slate-700 text-white" : "bg-[#f2f7ec]"}`} data-testid={`report-${i}-${k}`}>
                <p className="font-display text-2xl font-bold">{fmt ? fmt(r.summary[k] || 0) : r.summary[k] || 0}</p><p className="text-[11px] opacity-70">{l}</p>
              </div>
            ))}
          </div>
          {r.overtime.some((o) => o.status === "accettato") && (
            <p className="mt-3 text-xs text-slate-600" data-testid={`report-${i}-overtime-list`}>Straordinari accettati: {r.overtime.filter((o) => o.status === "accettato").map((o) => `${fmtDate(o.date, { day: "2-digit", month: "2-digit" })} ${o.start}-${o.end}`).join(", ")}</p>
          )}
          <DayDetail days={r.days} i={i} />
          <div className="mt-4 divide-y rounded-xl border">
            {r.absences.length ? r.absences.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span><b>{ABSENCE_TYPES[a.type]}</b> <span className="font-mono text-xs text-slate-500">{absenceSpan(a)}</span>{a.reason && <span className="ml-2 text-slate-500">· {a.reason}</span>}</span>
                <StatusBadge status={a.status} />
              </div>
            )) : <p className="px-3 py-3 text-sm text-slate-500" data-testid={`report-${i}-no-absences`}>Nessuna assenza nel periodo.</p>}
          </div>
        </section>
      )) : <p className="text-sm text-slate-500" data-testid="report-empty">Nessun risultato.</p>)}
    </div>
  );
}

export default function AdminAttendance() {
  return (
    <div>
      <PageHeader eyebrow="Personale" title="Calendario presenze" />
      <Tabs defaultValue="week">
        <TabsList className="mb-5">
          <TabsTrigger value="week" data-testid="attendance-tab-week">Vista settimanale</TabsTrigger>
          <TabsTrigger value="report" data-testid="attendance-tab-report">Interroga periodo</TabsTrigger>
        </TabsList>
        <TabsContent value="week"><WeekView /></TabsContent>
        <TabsContent value="report"><PeriodReport /></TabsContent>
      </Tabs>
    </div>
  );
}
